import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits } from "@saas/credits/hooks/use-credits";
import { useRouter } from "@shared/hooks/router";
import { useLocalStorage } from "@shared/hooks/use-local-storage";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type {
	GeneratedDescription,
	PromptLanguage,
	PromptType,
} from "../types";

export function useAiDescribeImage() {
	const [imageUrl, setImageUrl] = useState<string>("");
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreviewUrl, setImagePreviewUrl] = useState<string>("");
	const [selectedPromptType, setSelectedPromptType] =
		useState<PromptType>("detail");
	const [promptLanguage, setPromptLanguage] = useState<PromptLanguage>("en");
	const [generatedDescription, setGeneratedDescription] =
		useState<string>("");
	const [customQuestion, setCustomQuestion] = useState<string>("");
	const [descriptionHistory, setDescriptionHistory] = useState<
		GeneratedDescription[]
	>([]);
	const { savePrompt } = useLocalStorage();
	const { user } = useSession();
	const router = useRouter();
	const {
		getRemainingCredits,
		hasUnlimitedCredits,
		isLoading: isCreditsLoading,
		useCredit,
	} = useCredits();

	// 转换图像为Base64
	const convertImageToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64String = reader.result as string;
				const base64 = base64String.split(",")[1];
				resolve(base64);
			};
			reader.onerror = (error) => reject(error);
		});
	};

	// 处理图像选择
	const handleImageSelected = (file: File | null, url: string) => {
		if (file) {
			setImageFile(file);
			const previewUrl = URL.createObjectURL(file);
			setImagePreviewUrl(previewUrl);
			setImageUrl("");
		} else if (url) {
			setImageUrl(url);
			setImagePreviewUrl(url);
			setImageFile(null);
		} else {
			// 清除所有图片状态
			setImageFile(null);
			setImageUrl("");
			setImagePreviewUrl("");
		}
	};

	// 生成描述mutation
	const generateDescriptionMutation = useMutation({
		mutationFn: async (requestData: {
			imageUrl?: string;
			imageBase64?: string;
			promptType: PromptType;
			language: PromptLanguage;
			customQuestion?: string;
		}) => {
			// 使用image-description API端点
			const response = await fetch("/api/image-description", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestData),
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("Failed to generate description");
			}

			return response.json();
		},
		onSuccess: async (data) => {
			const newDescription = data.description;
			const originalDescription = data.originalDescription;
			setGeneratedDescription(newDescription);

			// 保存到历史记录
			if (imageFile || imageUrl) {
				let base64 = "";

				if (imageFile) {
					base64 = await convertImageToBase64(imageFile);
				} else if (imageUrl) {
					try {
						const response = await fetch(imageUrl);
						const blob = await response.blob();
						const reader = new FileReader();
						base64 = await new Promise<string>((resolve) => {
							reader.onload = () => {
								const base64String = reader.result as string;
								resolve(base64String.split(",")[1]);
							};
							reader.readAsDataURL(blob);
						});
					} catch (error) {
						console.error(
							"Error converting image URL to base64:",
							error,
						);
					}
				}

				// 保存到IndexedDB
				await savePrompt(
					newDescription,
					originalDescription,
					base64,
					selectedPromptType as any,
				);

				// 添加到历史记录
				const historyItem: GeneratedDescription = {
					promptType: selectedPromptType,
					description: newDescription,
					originalDescription: originalDescription,
					language: promptLanguage,
					timestamp: new Date(),
					customQuestion:
						selectedPromptType === "custom"
							? customQuestion
							: undefined,
				};

				setDescriptionHistory((prev) => [historyItem, ...prev]);
			}
		},
		onError: (error) => {
			toast.error("Error generating description");
			console.error(error);
		},
	});

	// 处理生成描述
	const handleGenerateDescription = async () => {
		if (generateDescriptionMutation.isPending || isCreditsLoading) return;

		// 检查用户是否已登录
		if (!user) {
			const returnUrl = "/ai-describe-image";
			router.push(
				`/auth/login?redirectTo=${encodeURIComponent(returnUrl)}`,
			);
			return;
		}

		// 检查是否有足够的 IMAGE_TO_TEXT 信用额度
		const isUnlimited = hasUnlimitedCredits("IMAGE_TO_TEXT");
		const remainingCredits = getRemainingCredits("IMAGE_TO_TEXT");
		if (!isUnlimited && remainingCredits <= 0) {
			toast.error("You have used all your credits");
			return;
		}

		// 检查是否有图像
		if (!imageFile && !imageUrl) {
			toast.error("Please upload an image or provide an image URL");
			return;
		}

		// 检查自定义问题
		if (selectedPromptType === "custom" && !customQuestion.trim()) {
			toast.error("Please enter your question");
			return;
		}

		try {
			const requestData: {
				imageUrl?: string;
				imageBase64?: string;
				promptType: PromptType;
				language: PromptLanguage;
				customQuestion?: string;
			} = {
				promptType: selectedPromptType,
				language: promptLanguage,
			};

			if (selectedPromptType === "custom") {
				requestData.customQuestion = customQuestion;
			}

			if (imageFile) {
				const base64 = await convertImageToBase64(imageFile);
				requestData.imageBase64 = base64;
			} else if (imageUrl) {
				requestData.imageUrl = imageUrl;
			}

			generateDescriptionMutation.mutate(requestData);
		} catch (error) {
			toast.error("Error preparing image data");
			console.error(error);
		}
	};

	// 获取剩余信用额度，对无限额度进行处理
	const getRemainingCreditsFormatted = () => {
		if (hasUnlimitedCredits("IMAGE_TO_TEXT")) {
			return "infinity"; // 特殊标记，表示无限
		}
		return getRemainingCredits("IMAGE_TO_TEXT");
	};

	// 处理获取更多积分
	const handleGetMoreCredits = () => {
		router.push("/pricing");
	};

	return {
		imageUrl,
		imageFile,
		imagePreviewUrl,
		selectedPromptType,
		promptLanguage,
		generatedDescription,
		customQuestion,
		descriptionHistory,
		usesRemaining: getRemainingCreditsFormatted(),
		hasUnlimitedCredits: () => hasUnlimitedCredits("IMAGE_TO_TEXT"),
		isPending: generateDescriptionMutation.isPending || isCreditsLoading,
		handleImageSelected,
		handleGenerateDescription,
		handleGetMoreCredits,
		setSelectedPromptType,
		setPromptLanguage,
		setCustomQuestion,
		setDescriptionHistory,
	};
}
